// teamsFormatter.js - Enhanced version with PROPER heading support for Teams
import DOMPurify from 'dompurify';
 
/**
 * Converts Quill HTML to Teams-compatible HTML with FULL formatting support
 * Including: headers, colors, indentation, lists, links, etc.
 */
export const convertQuillToTeamsHTML = (htmlContent) => {
  if (!htmlContent) return '';
 
  let sanitized = DOMPurify.sanitize(htmlContent, {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: [
      'p', 'div', 'br', 'span',
      'strong', 'b', 'em', 'i', 'u', 's', 'strike',
      'ul', 'ol', 'li',
      'a', 'img',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'blockquote', 'pre', 'code',
      'font' // For color support
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'color', 'size']
  });
 
  let teamsHTML = sanitized;
 
  // ✅ CRITICAL FIX: Convert headings to Teams-compatible format with inline styles
  // Teams requires explicit styling for headings to render properly
  teamsHTML = teamsHTML.replace(
    /<h1>(.*?)<\/h1>/gi,
    '<p style="font-size: 24px; font-weight: bold; margin: 16px 0 8px 0; line-height: 1.3;">$1</p>'
  );
  
  teamsHTML = teamsHTML.replace(
    /<h2>(.*?)<\/h2>/gi,
    '<p style="font-size: 20px; font-weight: bold; margin: 14px 0 7px 0; line-height: 1.3;">$1</p>'
  );
  
  teamsHTML = teamsHTML.replace(
    /<h3>(.*?)<\/h3>/gi,
    '<p style="font-size: 18px; font-weight: bold; margin: 12px 0 6px 0; line-height: 1.3;">$1</p>'
  );
  
  teamsHTML = teamsHTML.replace(
    /<h4>(.*?)<\/h4>/gi,
    '<p style="font-size: 16px; font-weight: bold; margin: 10px 0 5px 0; line-height: 1.3;">$1</p>'
  );
  
  teamsHTML = teamsHTML.replace(
    /<h5>(.*?)<\/h5>/gi,
    '<p style="font-size: 14px; font-weight: bold; margin: 8px 0 4px 0; line-height: 1.3;">$1</p>'
  );
  
  teamsHTML = teamsHTML.replace(
    /<h6>(.*?)<\/h6>/gi,
    '<p style="font-size: 13px; font-weight: bold; margin: 6px 0 3px 0; line-height: 1.3;">$1</p>'
  );
 
  // ENHANCED: Handle Quill's indent classes for deeply nested lists
  teamsHTML = teamsHTML.replace(
    /<li class="ql-indent-(\d+)">/gi,
    (match, level) => {
      const indentLevel = parseInt(level);
      return `<li style="margin-left: ${indentLevel * 30}px;">`;
    }
  );
 
  // Handle Quill color classes (if using color picker)
  teamsHTML = teamsHTML.replace(
    /<span class="ql-color-([^"]+)">/gi,
    (match, color) => `<span style="color: ${color};">`
  );
 
  // Handle Quill background color
  teamsHTML = teamsHTML.replace(
    /<span class="ql-bg-([^"]+)">/gi,
    (match, color) => `<span style="background-color: ${color};">`
  );
 
  // Clean up whitespace in lists
  teamsHTML = teamsHTML.replace(/<ul>\s+/g, '<ul>');
  teamsHTML = teamsHTML.replace(/<ol>\s+/g, '<ol>');
  teamsHTML = teamsHTML.replace(/\s+<\/ul>/g, '</ul>');
  teamsHTML = teamsHTML.replace(/\s+<\/ol>/g, '</ol>');
 
  // Handle empty paragraphs
  teamsHTML = teamsHTML.replace(/<p>\s*(&nbsp;)?\s*<\/p>/gi, '<br/>');
 
  // Prevent excessive line breaks
  teamsHTML = teamsHTML.replace(/(<br\s*\/?>){3,}/gi, '<br/><br/>');
 
  // Normalize spaces
  teamsHTML = teamsHTML.replace(/&nbsp;|\u00A0/g, ' ');
 
  return teamsHTML.trim();
};
 
/**
 * Extracts plain text from HTML (for notifications/fallback)
 */
export const extractPlainText = (htmlContent) => {
  if (!htmlContent) return '';
 
  let text = htmlContent;
 
  // Preserve links with their URLs
  text = text.replace(
    /<a\s+[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi,
    (match, href, innerText) => `${innerText} (${href})`
  );
 
  // Remove style and script tags
  text = text.replace(/<style[^>]*>.*?<\/style>/gis, '');
  text = text.replace(/<script[^>]*>.*?<\/script>/gis, '');
 
  // Convert block elements to newlines
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<\/p>/gi, '\n');
  text = text.replace(/<\/div>/gi, '\n');
  text = text.replace(/<\/h[1-6]>/gi, '\n');
  text = text.replace(/<\/li>/gi, '\n');
 
  // Remove all remaining HTML tags
  text = text.replace(/<[^>]+>/g, '');
 
  // Decode HTML entities
  text = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
 
  // Clean up excessive newlines
  text = text.replace(/\n{3,}/g, '\n\n').trim();
 
  return text;
};
 
/**
 * Converts Quill HTML to Markdown for Adaptive Cards
 * ENHANCED: Better heading and indentation handling
 */
export function convertQuillToAdaptiveCardText(htmlContent) { 
  if (!htmlContent) {  
    return '';  
  }  
 
  const strippedHtml = htmlContent
    // Convert headings to markdown with proper spacing
    .replace(/<h1>(.*?)<\/h1>/gi, '\n\n# $1\n\n')
    .replace(/<h2>(.*?)<\/h2>/gi, '\n\n## $1\n\n')
    .replace(/<h3>(.*?)<\/h3>/gi, '\n\n### $1\n\n')
    .replace(/<h4>(.*?)<\/h4>/gi, '\n\n#### $1\n\n')
    .replace(/<h5>(.*?)<\/h5>/gi, '\n\n##### $1\n\n')
    .replace(/<h6>(.*?)<\/h6>/gi, '\n\n###### $1\n\n')
    .replace(/<br\s*\/?>/gi, '\n')  
    .replace(/<\/p>\s*<p>/gi, '\n\n')  
    .replace(/<\/div>\s*<div>/gi, '\n\n')
    // ENHANCED: Better indent handling - each level adds 2 spaces
    .replace(/<li class="ql-indent-(\d+)">/gi, (_, level) => {
      const indent = '  '.repeat(parseInt(level));
      return '\n' + indent + '- ';
    })
    .replace(/<\/li>\s*<li>/gi, '\n- ')  
    .replace(/<li>/gi, '\n- ')  
    .replace(/<\/li>/gi, '')
    .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')  
    .replace(/<b>(.*?)<\/b>/gi, '**$1**')
    .replace(/<em>(.*?)<\/em>/gi, '_$1_')  
    .replace(/<i>(.*?)<\/i>/gi, '_$1_')
    .replace(/<u>(.*?)<\/u>/gi, '$1')  
    .replace(/<a[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
    .replace(/<img[^>]*>/gi, '')  
    .replace(/<[^>]*>/g, '');  
 
  let resultText = strippedHtml
    .replace(/\r\n/g, '\n')  
    .replace(/\n{3,}/g, '\n\n')
    .trim();
 
  return resultText;  
}
 
/**
 * Converts Quill HTML to Adaptive Card JSON
 */
export const convertQuillToAdaptiveCard = (htmlContent, title = '') => {
  if (!htmlContent) return null;
 
 const markdown = convertQuillToAdaptiveCardText(htmlContent);
  if (!markdown) return null;
 
  const card = {
    type: 'AdaptiveCard',
    $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
    version: '1.5',
    body: []
  };
 
  if (title) {
    card.body.push({
      type: 'TextBlock',
      text: title,
      weight: 'Bolder',
      size: 'Large',
      wrap: true,
      separator: true
    });
  }
 
  // Single TextBlock with full markdown content preserves ALL spacing
  card.body.push({
    type: 'TextBlock',
    text: markdown,
    wrap: true
  });
 
  return card;
};
 
/**
 * NEW: Add @mentions to the message
 */
export const addMentionsToPayload = (mentions) => {
  if (!mentions || mentions.length === 0) return null;
 
  return mentions.map(mention => ({
    type: 'mention',
    mentioned: {
      id: mention.id,
      name: mention.name
    },
    text: `<at>${mention.name}</at>`
  }));
};
 
/**
 * NEW: Create action buttons for the message
 */
export const createActionButtons = (actions) => {
  if (!actions || actions.length === 0) return null;
 
  return actions.map(action => ({
    type: 'Action.OpenUrl',
    title: action.title,
    url: action.url
  }));
};
 
/**
 * ENHANCED: Creates the complete Teams Send Activity payload with NEW features
 */
export const createTeamsPayload = (
  subject,
  htmlContent,
  sendTo,
  attachments = [],
  optionalEditors = [],
  options = {}
) => {
  const teamsHTML = convertQuillToTeamsHTML(htmlContent);
  const plainText = extractPlainText(htmlContent);

  // Base payload
  const payload = {
    sendTo: (typeof sendTo === 'string' ? sendTo : '')
      .split(';')
      .map(email => email.trim())
      .filter(Boolean),
    subject,
    plainText,
    content: teamsHTML,
    attachments: attachments || [],
    timestamp: new Date().toISOString(),
    // OptionalEditors removed — not needed in payload
  };

  // NEW: Add mentions if provided
  if (options.mentions && options.mentions.length > 0) {
    payload.mentions = addMentionsToPayload(options.mentions);
  }

  // NEW: Add action buttons if provided
  if (options.actions && options.actions.length > 0) {
    payload.actions = createActionButtons(options.actions);
  }

  // NEW: Add notification type
  if (options.notificationType) {
    payload.notificationType = options.notificationType;
  }

  return payload;
};
 
/**
 * Creates Teams payload WITH Adaptive Card attachment
 */
export const createTeamsPayloadWithAdaptiveCard = (
  subject,
  htmlContent,
  sendTo,
  attachments = [],
  optionalEditors = [],
  options = {}
) => {
  const plainText = extractPlainText(htmlContent);
  const [popup, link] = optionalEditors;
 
  const popupTitle = popup?.popup_title || '';
  const popupCard = (popupTitle && popup?.popup_content)
    ? convertQuillToAdaptiveCard(popup.popup_content, popupTitle)
    : null;
 
  const linkTitle = link?.Link_title || '';
  const linkContent = link?.Link_content || '';
 
  const adaptiveCardAttachments = [];
 
  // Add popup as Adaptive Card if available
  if (popupCard) {
    // Add action buttons to the card if provided
    if (options.actions && options.actions.length > 0) {
      popupCard.actions = createActionButtons(options.actions);
    }
 
    adaptiveCardAttachments.push({
      contentType: 'application/vnd.microsoft.card.adaptive',
      content: popupCard
    });
  }
 
  const payload = {
    sendTo: (typeof sendTo === 'string' ? sendTo : '')
      .split(';')
      .map(email => email.trim())
      .filter(Boolean),
    subject,
    plainText,
    attachments: [...adaptiveCardAttachments, ...(attachments || [])],
    timestamp: new Date().toISOString()
    // OptionalEditors: [
    //   {
    //     popup_title: popupTitle,
    //     popup_content: popupCard ? JSON.stringify(popupCard) : ''
    //   },
    //   {
    //     Link_title: linkTitle,
    //     Link_content: linkContent
    //   }
    // ]
  };
 
  // Add mentions if provided
  if (options.mentions && options.mentions.length > 0) {
    payload.mentions = addMentionsToPayload(options.mentions);
  }
 
  if (options.notificationType) {
    payload.notificationType = options.notificationType;
  }
 
  return payload;
};
 
/**
 * Create individual activity messages
 */
export const createActivityMessage = (content, attachments = [], options = {}) => {
  const teamsHTML = convertQuillToTeamsHTML(content);
  const plainText = extractPlainText(content);
 
  const activity = {
    type: 'message',
    text: plainText,
    textFormat: 'xml',
    channelData: {
      text: teamsHTML
    },
    importance: options.importance || 'normal'
  };
 
  if (attachments?.length > 0) {
    activity.attachments = attachments.map(attachment => ({
      contentType: 'image/png',
      contentUrl: attachment,
      name: 'attachment'
    }));
  }
 
  // Add mentions if provided
  if (options.mentions && options.mentions.length > 0) {
    activity.entities = addMentionsToPayload(options.mentions);
  }
 
  return activity;
};
 
/**
 * Create activity message with Adaptive Card
 */
export const createActivityMessageWithAdaptiveCard = (title, content, options = {}) => {
  const adaptiveCard = convertQuillToAdaptiveCard(content, title);
  const plainText = extractPlainText(content);
 
  if (!adaptiveCard) {
    return createActivityMessage(content, [], options);
  }
 
  // Add action buttons to the card if provided
  if (options.actions && options.actions.length > 0) {
    adaptiveCard.actions = createActionButtons(options.actions);
  }
 
  return {
    type: 'message',
    text: plainText,
    importance: options.importance || 'normal',
    attachments: [
      {
        contentType: 'application/vnd.microsoft.card.adaptive',
        content: adaptiveCard
      }
    ]
  };
};
 
/**
 * NEW: Helper to parse mentions from text
 */
export const extractMentionsFromText = (text, userDatabase = []) => {
  const mentionRegex = /@(\S+@\S+\.\S+)|@([A-Z][a-z]+\s[A-Z][a-z]+)/g;
  const mentions = [];
  let match;
 
  while ((match = mentionRegex.exec(text)) !== null) {
    const mentionText = match[1] || match[2];
   
    const user = userDatabase.find(u =>
      u.email === mentionText || u.name === mentionText
    );
 
    if (user) {
      mentions.push({
        id: user.email,
        name: user.name || user.email
      });
    }
  }
 
  return mentions;
};